import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Menu, MenuItem } from '@material-ui/core'
import { AuditLog } from 'Trunkrs-SDK/dist/models'
import get from 'lodash/get'
import map from 'lodash/map'
import moment from 'moment'

import { useSelector } from 'react-redux'
import * as shipmentsAction from 'actions/shipments'
import * as tourActions from 'actions/tours'
import * as UIActions from 'actions/ui'
import * as fromShipments from 'selectors/shipments'
import * as fromProfile from 'selectors/profile'
import * as fromTour from 'selectors/tour'
import * as fromCombined from 'selectors/combined'

import { Tour } from 'common/models/typings/Tour'
import { Driver } from 'common/models/typings/Driver'
import { NETWORK_TYPE } from 'common/models/typings/TourOverview'

import trunkrs, { responseHandler } from 'common/utils/Sdk'
import {
  getAvailablePrinters,
  printZpl,
  ZebraDevice,
} from 'common/utils/BrowserPrint'
import {
  buildHandoverLabelZpl,
  classifyHandoverException,
} from 'common/utils/HandoverLabelZpl'
import { readPrinterSettings } from 'common/components/features/Handover/PrinterSettings/usePrinterSettings'
import { notify } from 'utils/notify'

import Typography from 'common/components/base/Typography'
import ScanInput from 'common/components/base/forms/ScanInput'
import Button from 'common/components/base/Button'
import Icon from 'common/components/base/Icon'
import HandoverLocked from 'common/components/features/Handover/Locked'
import HandoverCart from 'common/components/features/Handover/Cart'

import HandoverDialogResult from 'common/components/features/Handover/Dialog/Result'
import HandoverDialogPrint from 'common/components/features/Handover/Dialog/Print'
import HandoverDialogChangeDriver from 'common/components/features/Handover/Dialog/ChangeDriver'
import HandoverPrinterSettings from 'common/components/features/Handover/PrinterSettings'
import { isPrinterFeatureEnabled } from 'common/components/features/Handover/PrinterSettings/printerFeature'
import * as fromNetworkType from 'selectors/networkType'
import useStyles from './useStyles'
import { FuelName, FuelType } from 'common/models/typings/FuelType'
import { useAppDispatch } from 'planner-upgrades/redux/hooks'

const HandoverPage = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const scanInputRef = React.useRef<HTMLInputElement>()
  const shipments = useSelector(fromShipments.getData)
  const barcodes = useSelector(fromShipments.getHandoverShipments)
  const subcoCurrentRegion = useSelector(fromProfile.getSubcoRegion)
  const subcoId = useSelector(fromProfile.getCurrentSubcoId)
  const tours: Tour[] = useSelector(fromCombined.getShipmentToursHandovered)
  const drivers: Record<string, Driver> = useSelector(
    (state: any) => state.drivers,
  )
  const toursPlanned = useSelector(fromCombined.areToursPlanned)
  const subcoProfile = useSelector(fromProfile.getSubco)
  // label printing pilot: only subcos with the database flag see the feature
  const printerFeatureEnabled = isPrinterFeatureEnabled(subcoProfile)

  const [value, setValue] = React.useState<string>('')
  const [lastShipment, setLastShipment] = React.useState<any>(null)
  const [isResultOpen, setIsResultOpen] = React.useState<boolean>(false)
  const [isError, setIsError] = React.useState<boolean>(false)
  const [isPrintOpen, setIsPrintOpen] = React.useState<boolean>(false)
  const [isPrinterSettingsOpen, setIsPrinterSettingsOpen] =
    React.useState<boolean>(false)
  const [recentScans, setRecentScans] = React.useState<any[]>([])
  const [recentScansAnchor, setRecentScansAnchor] =
    React.useState<null | HTMLElement>(null)
  const scanSequenceRef = React.useRef<number>(0)

  const [activeTour, setActiveTour] = React.useState<Tour | null>(null)
  const [isChangeDriver, setIsChangeDriver] = React.useState<boolean>(false)
  const networkType = useSelector(fromNetworkType.getNetworkType)

  const printerDeviceCache = React.useRef<{
    uid: string
    device: ZebraDevice
  } | null>(null)

  // Labels must leave the printer in scan order: on the belt each box travels
  // in scan order to the person applying labels. An exception label needs a
  // network lookup, so without a queue a later fast (normal) label could
  // overtake an earlier slow (exception) one and shift every parcel by one.
  // This FIFO queue runs every print job in the order it was enqueued.
  const printQueueRef = React.useRef<Promise<void>>(Promise.resolve())
  const enqueuePrint = React.useCallback((job: () => Promise<void>) => {
    // run each job after the previous settled, whatever its outcome
    printQueueRef.current = printQueueRef.current.then(job, job)
  }, [])

  const printLabelZpl = React.useCallback(
    async (zpl: string, options?: { manual?: boolean }) => {
      const settings = readPrinterSettings()
      // manual reprints work even when auto-print is off for this station;
      // outside the pilot allowlist nothing prints at all
      if (
        !printerFeatureEnabled ||
        (!settings.autoPrintEnabled && !options?.manual)
      ) {
        return
      }

      try {
        let device: ZebraDevice | undefined
        if (settings.printerUid) {
          if (printerDeviceCache.current?.uid === settings.printerUid) {
            device = printerDeviceCache.current.device
          } else {
            const printers = await getAvailablePrinters()
            device = printers.find(
              (printer) => printer.uid === settings.printerUid,
            )
            if (device) {
              printerDeviceCache.current = { uid: device.uid, device }
            }
          }
        }

        // an unplugged selected printer falls back to the agent's default
        await printZpl(zpl, device)
        if (options?.manual) {
          notify('PRINTER_REPRINT_SENT', 'PRINTER_REPRINT_SENT_MSG')
        }
      } catch (error) {
        printerDeviceCache.current = null
        notify('PRINTER_ERROR', 'PRINTER_ERROR_MSG', false)
      }
    },
    [printerFeatureEnabled],
  )

  const printHandoverLabel = React.useCallback(
    async (
      shipment: any,
      tour: Tour,
      position: number,
      multicolli?: { index: number; total: number } | null,
      options?: { manual?: boolean },
    ) => {
      await printLabelZpl(
        buildHandoverLabelZpl({
          tourNumber: get(tour, 'number', ''),
          stopNumber: position ?? '',
          parcelId: get(shipment, 'trunkrsNr'),
          date: moment().format('DD-MM-YYYY'),
          isDayDelivery: networkType === NETWORK_TYPE.DAY,
          isPriority: !!get(shipment, 'isPriority'),
          leaveBehindPermission: !!get(shipment, 'hasLeaveBehindPermission'),
          leaveBehindRemark: get(shipment, 'leaveBehindRemark'),
          multicolli,
          remark: get(shipment, 'remarks'),
        }),
        options,
      )
    },
    [printLabelZpl, networkType],
  )

  // A scanned parcel with no tour in this handover still gets a label so the
  // belt stays 1:1. The reason is resolved best-effort from the hub log; if
  // that fails we still print (GEEN RIT when the parcel exists, else NIET
  // GEVONDEN). knownInstance comes from the missort lookup so we don't refetch.
  const printExceptionLabel = React.useCallback(
    async (barcode: string, knownInstance?: any) => {
      const settings = readPrinterSettings()
      if (!printerFeatureEnabled || !settings.autoPrintEnabled) {
        return
      }

      let reason = ''
      let parcelId: string | undefined = get(knownInstance, 'trunkrsNr')
      try {
        const logs = await trunkrs
          .Shipment()
          .fetchHubLogsBySubCoId(subcoId, barcode)
        const item =
          (logs || []).find(
            (log: any) =>
              log.barcode === barcode || String(log.trunkrsNr) === barcode,
          ) || (logs || [])[0]
        if (item?.shipmentState) {
          reason = classifyHandoverException(item.shipmentState)
          parcelId = item.trunkrsNr ?? parcelId
        }
      } catch (error) {
        // best-effort: fall through to the existence-based fallback
      }

      if (!reason) {
        reason = knownInstance ? 'GEEN RIT' : 'NIET GEVONDEN'
      }

      await printLabelZpl(
        buildHandoverLabelZpl({
          tourNumber: '',
          stopNumber: '',
          parcelId,
          exception: { reason, barcode },
        }),
      )
    },
    [printLabelZpl, printerFeatureEnabled, subcoId],
  )

  const addMissorted = React.useCallback(
    async (barcode: string) => {
      dispatch(UIActions.setLoading(true))
      const [error, shipmentInstance] = await responseHandler(
        trunkrs.Shipment().fetchByBarcode(barcode),
      )

      if (error) {
        AuditLog.logShipmentByBarcode(barcode)
      }

      if (shipmentInstance) {
        shipmentInstance.isMissorted(subcoCurrentRegion, subcoId)
      }

      dispatch(UIActions.setLoading(false))
      return shipmentInstance
    },
    [dispatch, subcoCurrentRegion, subcoId],
  )

  const handleToggleDialog = React.useCallback((open?: boolean) => {
    if (open !== undefined) {
      setIsResultOpen(open)
    } else {
      setIsResultOpen((prev) => !prev)
    }
  }, [])

  const handleLockTours = React.useCallback(() => {
    dispatch(tourActions.lockTours(networkType))
  }, [dispatch, networkType])

  const handleHandover = React.useCallback(
    (shipmentId: number) => {
      dispatch(shipmentsAction.handOverShipment(shipmentId))
    },
    [dispatch],
  )

  const handleScan = React.useCallback(
    async (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.getModifierState('CapsLock')) {
        alert(t('CAPSLOCK_ON'))
        setValue('')
        return
      }
      if (event.key === 'Enter' && value) {
        // close dialog if open
        handleToggleDialog(false)

        event.preventDefault()
        const shipmentId = barcodes[value]

        if (!shipmentId) {
          const scannedValue = value
          setIsError(true)
          // register the missort as before, then print an exception label so
          // the belt stays 1:1. Enqueue synchronously (before any await) so
          // prints keep scan order; the print awaits the same lookup.
          const missortLookup = addMissorted(scannedValue)
          enqueuePrint(async () => {
            const instance = await missortLookup
            await printExceptionLabel(scannedValue, instance)
          })
        } else {
          const shipment = shipments[shipmentId]
          const tour =
            tours.find((tr) => tr.id === shipment.tour.id) || ({} as Tour)
          const driver = drivers[tour.driverId]
          const position = get(shipments, `${shipmentId}.tour.position`)

          // multicolli: all parcels on the same stop of the same tour
          const siblingIds =
            position != null
              ? Object.values(shipments)
                  .filter(
                    (sib: any) =>
                      get(sib, 'tour.id') != null &&
                      get(sib, 'tour.id') === get(shipment, 'tour.id') &&
                      get(sib, 'tour.position') === position,
                  )
                  .map((sib: any) => sib.id)
                  .sort((a: number, b: number) => a - b)
              : []
          const multicolli =
            siblingIds.length > 1
              ? {
                  index: siblingIds.indexOf(shipment.id) + 1,
                  total: siblingIds.length,
                }
              : null

          setIsError(false)
          setLastShipment({
            ...shipment,
            position,
            tour,
            driver,
            multicolli,
          })
          handleHandover(shipmentId)
          scanSequenceRef.current += 1
          setRecentScans((prev) =>
            [
              {
                seq: scanSequenceRef.current,
                shipment,
                tour,
                position,
                multicolli,
              },
              ...prev,
            ].slice(0, 10),
          )
          // fire-and-forget through the FIFO queue: printing never blocks or
          // delays scanning, but labels still leave the printer in scan order
          enqueuePrint(() =>
            printHandoverLabel(shipment, tour, position, multicolli),
          )
        }
        setValue('')
        handleToggleDialog()
      }
    },
    [
      addMissorted,
      barcodes,
      drivers,
      enqueuePrint,
      handleHandover,
      handleToggleDialog,
      printExceptionLabel,
      printHandoverLabel,
      shipments,
      tours,
      value,
    ],
  )

  const handleToggleChangeDriver = React.useCallback(
    (tour?: Tour) => {
      if (isChangeDriver) {
        setActiveTour(null)
      } else if (tour) {
        setActiveTour(tour)
      }
      setIsChangeDriver((prev) => !prev)
    },
    [isChangeDriver],
  )

  const handleTogglePrinting = React.useCallback(() => {
    setIsPrintOpen((prev) => !prev)
  }, [])

  const handleTogglePrinterSettings = React.useCallback(() => {
    setIsPrinterSettingsOpen((prev) => !prev)
  }, [])

  const handleChangeDriver = React.useCallback(
    (driverId: number | null, vehicleType: FuelName) => {
      setActiveTour((prev) =>
        prev !== null
          ? { ...prev, driverId: driverId || -1, vehicleType }
          : null,
      )
    },
    [],
  )

  const handleSave = React.useCallback(async () => {
    if (activeTour) {
      await dispatch(
        tourActions.saveDriverToTour(
          activeTour.id,
          activeTour.driverId,
          activeTour.vehicleType,
        ),
      )
      handleToggleChangeDriver()
    }
  }, [activeTour, dispatch, handleToggleChangeDriver])

  const carts = React.useMemo(
    () =>
      map(
        tours,
        (tour, index) =>
          tour.driverId &&
          drivers && (
            <HandoverCart
              key={tour.id}
              index={Number(index)}
              tour={tour}
              driver={drivers[tour.driverId]}
              onHandover={handleHandover}
              onToggleChangeDriver={() => handleToggleChangeDriver(tour)}
            />
          ),
      ),
    [drivers, handleHandover, handleToggleChangeDriver, tours],
  )

  if (!toursPlanned) {
    return <HandoverLocked onLockTours={handleLockTours} />
  }

  return (
    <div className="mr-[-32px]">
      <div className="flex flex-col overflow-y-scroll max-h-[100vh] pr-[32px]">
        <div className="flex items-center justify-between mb-4">
          <Box>
            <Typography variant="h4" transform="none">
              {t('READY_HANDOVER')}
            </Typography>
            <Typography color="theBeast"> {t('START_SCANNING')}</Typography>
          </Box>
          <div className="flex items-center gap-2">
            {printerFeatureEnabled && recentScans.length > 0 && (
              <Button
                color="white"
                className={classes.button}
                startIcon={<Icon name="retry" />}
                onClick={(event: React.MouseEvent<HTMLElement>) =>
                  setRecentScansAnchor(event.currentTarget)
                }
              >
                {t('RECENT_SCANS')}
              </Button>
            )}
            {printerFeatureEnabled && (
              <Button
                color="white"
                className={classes.button}
                startIcon={<Icon name="print" fill="currentColor" />}
                onClick={handleTogglePrinterSettings}
              >
                {t('PRINTER_SETTINGS')}
              </Button>
            )}
            <Button
              color="white"
              className={classes.button}
              startIcon={<Icon name="print" fill="currentColor" />}
              onClick={handleTogglePrinting}
            >
              {t('EXPORT')}
            </Button>
          </div>
        </div>
        <div className="h-8" />
        <div className="px-0.5">
          <ScanInput
            fullWidth
            size="large"
            placeholder={t('BARCODE')}
            value={value}
            onChange={(ev) => setValue(ev.target.value)}
            onKeyPress={handleScan}
            inputRef={scanInputRef}
          />
        </div>
        <div className="h-6" />
        <div className="grid grid-cols-4 gap-4 mb-36 mt-4">{carts}</div>
      </div>
      <HandoverDialogResult
        shipment={lastShipment}
        error={isError}
        open={isResultOpen}
        onClose={handleToggleDialog}
        onReprint={
          printerFeatureEnabled
            ? () =>
                printHandoverLabel(
                  lastShipment,
                  get(lastShipment, 'tour'),
                  get(lastShipment, 'position'),
                  get(lastShipment, 'multicolli'),
                  { manual: true },
                )
            : undefined
        }
        TransitionProps={{ onEntered: () => scanInputRef.current?.focus() }}
      />
      <Menu
        anchorEl={recentScansAnchor}
        open={!!recentScansAnchor}
        onClose={() => setRecentScansAnchor(null)}
      >
        {recentScans.map((scan) => (
          <MenuItem
            key={scan.seq}
            onClick={() => {
              setRecentScansAnchor(null)
              printHandoverLabel(
                scan.shipment,
                scan.tour,
                scan.position,
                scan.multicolli,
                { manual: true },
              )
            }}
          >
            <div className="flex items-center gap-2">
              <Icon name="print" style={{ height: 16, width: 16 }} />
              {`${t('TOUR')} ${get(scan, 'tour.number', '?')} · STOP ${
                scan.position ?? '?'
              } · ${get(scan, 'shipment.recipient.name', '')}`}
            </div>
          </MenuItem>
        ))}
      </Menu>
      <HandoverDialogPrint open={isPrintOpen} onClose={handleTogglePrinting} />
      <HandoverPrinterSettings
        open={isPrinterSettingsOpen}
        onClose={handleTogglePrinterSettings}
      />
      {activeTour && (
        <HandoverDialogChangeDriver
          open={isChangeDriver}
          tour={activeTour}
          onChangeDriver={handleChangeDriver}
          onSave={handleSave}
          onClose={handleToggleChangeDriver}
        />
      )}
    </div>
  )
}

export default HandoverPage
